import { useRef, useState } from "react";

/**
 * Tracks which download is in flight, so a control can show it is working.
 *
 * The server renders an entire document before returning a byte, and on a cold
 * start also loads a rasteriser and its fonts. Without this a click looks inert
 * for several seconds and invites a second one.
 *
 * `key` identifies the control, so a screen with several of them (a Word and a
 * PDF button, or one icon per chapter row) can disable them all while marking
 * only the one that was pressed.
 */
export function useDownload<TKey extends string = string>() {
  const [pending, setPending] = useState<TKey | null>(null);
  // A ref, not the state, decides whether a download is already running: two
  // clicks in the same tick would both read the pre-render state and start.
  const running = useRef(false);

  const start = (key: TKey, action: () => void | Promise<void>) => {
    if (running.current) return;
    running.current = true;
    setPending(key);

    void (async () => {
      try {
        await action();
      } finally {
        running.current = false;
        // Deliberately no catch: a failure still rejects, so the global
        // unhandledrejection handler in main.tsx raises the error toast it
        // always has. Swallowing it here would make a failed download look
        // like nothing happened at all.
        setPending(null);
      }
    })();
  };

  return { pending, busy: pending !== null, start };
}
